import {useCallback, useEffect, useRef, useState} from "react";

type SelectionId = number | string;

interface UsePersistedSelectionOptions<T extends SelectionId> {
    storageKey: string;
    allOptionIds: T[];
    storage?: Storage;
    /**
     * Indica que as opções terminaram de carregar. Permite hidratar um conjunto
     * vazio (menu carregado sem opções) em vez de aguardar opções indefinidamente.
     */
    optionsLoaded?: boolean;
}

interface UsePersistedSelectionResult<T extends SelectionId> {
    selectedIds: T[];
    setSelectedIds: (ids: T[]) => void;
    isInitialized: boolean;
    /**
     * `true` somente depois que a seleção persistida foi validada contra as opções
     * carregadas, ou quando as opções terminaram de carregar vazias.
     */
    isHydrated: boolean;
}

const readFromStorage = <T extends SelectionId>(storage: Storage, key: string): T[] | null => {
    try {
        const raw = storage.getItem(key);
        if (raw == null) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed as T[];
    } catch {
        return null;
    }
};

const writeToStorage = <T extends SelectionId>(storage: Storage, key: string, value: T[]) => {
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch {
        /* noop — storage indisponível ou cheio */
    }
};

export function usePersistedSelection<T extends SelectionId>(
    options: UsePersistedSelectionOptions<T>
): UsePersistedSelectionResult<T> {
    const {storageKey, allOptionIds, optionsLoaded = false} = options;
    const storage = options.storage ?? (typeof window !== "undefined" ? window.sessionStorage : undefined);

    const [selectedIds, setSelectedIdsState] = useState<T[]>(() =>
        storage ? (readFromStorage<T>(storage, storageKey) ?? []) : []
    );
    const [isInitialized, setIsInitialized] = useState<boolean>(() =>
        !!storage && readFromStorage(storage, storageKey) != null
    );
    const [isHydrated, setIsHydrated] = useState(false);
    const hasHydratedRef = useRef(false);

    useEffect(() => {
        if (hasHydratedRef.current) return;
        if (allOptionIds.length === 0) {
            if (optionsLoaded) {
                setSelectedIdsState([]);
                setIsHydrated(true);
            }
            return;
        }

        const stored = storage ? readFromStorage<T>(storage, storageKey) : null;
        if (stored == null) {
            setSelectedIdsState(allOptionIds);
        } else {
            const allOptionsSet = new Set<T>(allOptionIds);
            const filtered = stored.filter((id) => allOptionsSet.has(id));
            setSelectedIdsState(filtered.length > 0 ? filtered : allOptionIds);
        }

        hasHydratedRef.current = true;
        setIsInitialized(true);
        setIsHydrated(true);
    }, [allOptionIds, optionsLoaded, storage, storageKey]);

    const setSelectedIds = useCallback(
        (ids: T[]) => {
            setSelectedIdsState(ids);
            if (storage) {
                writeToStorage(storage, storageKey, ids);
            }
        },
        [storage, storageKey]
    );

    return {selectedIds, setSelectedIds, isInitialized, isHydrated};
}
